// ---------------------------------------------------------------------------
// Centralized application bootstrap.
//
// ROOT CAUSE THIS FILE ADDRESSES
// The Android app loads the deployed site remotely (capacitor.config server.url).
// On a cold launch the WebView has an empty HTTP cache, so:
//   native splash (auto-hid after ~500ms) -> WebView still fetching  = BLACK
//   -> SSR html arrives, but the app shell is client-rendered        = WHITE
//   -> if any JS chunk / auth / settings request stalls, nothing ever
//      replaced that white page and there was no retry affordance.
// On a warm launch the assets are cached, so it "worked the second time".
//
// The fix: one boot state machine that ALWAYS resolves, a visible branded
// startup screen from the very first paint, offline/retry UI, and a hard
// fail-safe timeout. No business logic (wallet / games / results / admin)
// is touched by this module.
// ---------------------------------------------------------------------------

export type BootStatus = "starting" | "ready" | "offline" | "recoverable-error";

export type BootPhase =
  | "APP_BOOT_STARTED"
  | "REACT_ROOT_MOUNTED"
  | "NATIVE_INIT_STARTED"
  | "NATIVE_INIT_COMPLETED"
  | "AUTH_RESTORE_STARTED"
  | "AUTH_RESTORE_COMPLETED"
  | "MINIMUM_DATA_LOAD_STARTED"
  | "MINIMUM_DATA_LOAD_COMPLETED"
  | "ROUTER_READY"
  | "APP_READY"
  | "STARTUP_TIMEOUT"
  | "STARTUP_ERROR";

export type BootState = {
  status: BootStatus;
  phase: BootPhase;
  error: string | null;
  slow: boolean;
  attempt: number;
};

/** Hard fail-safe. Only a safety net — the happy path resolves in ms. */
const BOOT_TIMEOUT_MS = 9000;
/** When to switch the startup copy to "Taking longer than expected…". */
const SLOW_AFTER_MS = 3500;

const listeners = new Set<(s: BootState) => void>();

let state: BootState = {
  status: "starting",
  phase: "APP_BOOT_STARTED",
  error: null,
  slow: false,
  attempt: 0,
};

let bootstrapPromise: Promise<void> | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;
let slowId: ReturnType<typeof setTimeout> | null = null;
let netListenersInstalled = false;

const seenPhases = new Set<string>();

const STAGE_MAP: Record<string, import("@/lib/diagnostics/perf").StartupStage> = {
  APP_BOOT_STARTED: "shell_loaded",
  REACT_ROOT_MOUNTED: "react_mounted",
  NATIVE_INIT_COMPLETED: "network_detected",
  AUTH_RESTORE_STARTED: "auth_restore_started",
  AUTH_RESTORE_COMPLETED: "auth_restore_completed",
  MINIMUM_DATA_LOAD_COMPLETED: "settings_loaded",
  APP_READY: "home_usable",
};

// Every phase used to spawn its own async IIFE with its own dynamic import.
// Those promises resolve in arbitrary order, so `home_usable` could be marked
// before `shell_loaded` — the rating logic then saw an incomplete timeline and
// fired a bogus critical `startup_slow`. One shared, ordered queue fixes that.
type PendingStage = { stage: import("@/lib/diagnostics/perf").StartupStage; at: number };
const pendingStages: PendingStage[] = [];
let perfModule: Promise<typeof import("@/lib/diagnostics/perf")> | null = null;
let flushing = false;

function nowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : 0;
}

function queueStage(stage: PendingStage["stage"], at: number) {
  pendingStages.push({ stage, at });
  if (flushing) return;
  flushing = true;
  void (async () => {
    try {
      perfModule ??= import("@/lib/diagnostics/perf");
      const { markStartupStage } = await perfModule;
      // Drain in insertion order; new marks queued meanwhile are picked up here.
      while (pendingStages.length) {
        const next = pendingStages.shift()!;
        markStartupStage(next.stage, next.at);
      }
    } catch {
      /* diagnostics must never break startup */
    } finally {
      flushing = false;
      if (pendingStages.length) queueStage(pendingStages.shift()!.stage, nowMs());
    }
  })();
}

/** Structured, de-duplicated startup logging (one line per phase per attempt). */
export function logPhase(phase: BootPhase, detail?: unknown) {
  const key = `${state.attempt}:${phase}`;
  if (seenPhases.has(key)) return;
  seenPhases.add(key);
  const line = `[boot] ${phase}`;
  if (phase === "STARTUP_TIMEOUT" || phase === "STARTUP_ERROR") {
    console.warn(line, detail ?? "");
  } else {
    console.info(line, detail ?? "");
  }
  // Capture the stage timestamp NOW, synchronously. The dynamic import below
  // can take hundreds of ms (chunk fetch on a cold load); stamping after it
  // collapsed every stage onto one late time and produced false startup_slow.
  const stageAt = nowMs();
  const stage = STAGE_MAP[phase];
  if (stage) {
    queueStage(stage, stageAt);
    return;
  }

  // Diagnostics are best-effort and must never block or break startup.
  void (async () => {
    try {
      if (phase === "STARTUP_TIMEOUT" || phase === "STARTUP_ERROR") {
        const [{ report }, { slowestStage, getStartupTimeline }] = await Promise.all([
          import("@/lib/diagnostics/report"),
          import("@/lib/diagnostics/perf"),
        ]);
        report({
          category: "startup",
          code: phase === "STARTUP_TIMEOUT" ? "startup_slow" : "startup_failed",
          stage: slowestStage() ?? phase,
          severity: "critical",
          retryable: true,
          technical_message: typeof detail === "string" ? detail : undefined,
          metadata: getStartupTimeline(),
        });
      }
    } catch {
      /* ignore */
    }
  })();
}



export function getBootState(): BootState {
  return state;
}

export function subscribeBoot(cb: (s: BootState) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function setState(patch: Partial<BootState>) {
  state = { ...state, ...patch };
  if (typeof window !== "undefined") {
    (window as any).__BC_BOOT = state;
  }
  listeners.forEach((l) => {
    try {
      l(state);
    } catch {
      /* a broken subscriber must never break boot */
    }
  });
}

function setPhase(phase: BootPhase, detail?: unknown) {
  logPhase(phase, detail);
  setState({ phase });
}

function clearTimers() {
  if (timeoutId) clearTimeout(timeoutId);
  if (slowId) clearTimeout(slowId);
  timeoutId = null;
  slowId = null;
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** Any startup fetch gets a bounded lifetime + a fallback value. */
async function withTimeout<T>(label: string, ms: number, op: (signal: AbortSignal) => Promise<T>, fallback: T): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await op(controller.signal);
  } catch (e) {
    console.warn(`[boot] ${label} failed/timed out — continuing`, e);
    return fallback;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Restores the Supabase session from local storage. Never network-blocking,
 * never throws, always resolves. Auth is the ONLY thing boot waits for.
 *
 * The supabase client chunk is fetched BEFORE the AUTH_RESTORE_STARTED mark:
 * on a cold Android launch that chunk download is pure network time the app
 * cannot influence, and folding it into the stage produced false
 * `startup_slow` / auth_restore incidents (e.g. 1382 ms) even though the
 * session read itself takes only a few ms. The whole operation still shares
 * one bounded timeout, so behaviour and fallbacks are unchanged.
 */
/**
 * Soft budget for the awaited part of session restore. When the stored access
 * token is expired, `getSession()` performs a network token refresh; on a cold
 * Android launch that refresh alone measured 1428 ms (PERF-3F831CC8) and blocked
 * boot. After this budget we stop *waiting* — the refresh keeps running and the
 * client's own auto-refresh + `onAuthStateChange` deliver the session. Route
 * guards still await `getSession()` themselves, so nothing is validated less.
 */
const AUTH_RESTORE_SOFT_BUDGET_MS = 700;

async function restoreAuthSafely(): Promise<void> {
  await withTimeout(
    "auth restore",
    4000,
    async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      setPhase("AUTH_RESTORE_STARTED");
      const session = supabase.auth.getSession();
      // Never let an unhandled rejection escape the detached promise.
      void session.catch(() => undefined);
      await Promise.race([
        session,
        new Promise((resolve) => setTimeout(resolve, AUTH_RESTORE_SOFT_BUDGET_MS)),
      ]);
    },
    undefined,
  );
  setPhase("AUTH_RESTORE_STARTED"); // no-op if already marked (de-duplicated)
  setPhase("AUTH_RESTORE_COMPLETED");
}


/**
 * Native side-effects (splash hide, back button). Failure is non-fatal:
 * plugin availability is checked inside native-shell itself.
 */
async function initNativeSafely(): Promise<void> {
  setPhase("NATIVE_INIT_STARTED");
  try {
    const { hideNativeSplash } = await import("@/lib/splash");
    await hideNativeSplash();
  } catch (e) {
    console.warn("[boot] splash hide skipped", e);
  }
  setPhase("NATIVE_INIT_COMPLETED");
}

async function performBootstrap(): Promise<void> {
  setState({ status: "starting", error: null, slow: false, attempt: state.attempt + 1 });
  setPhase("APP_BOOT_STARTED");
  installNetworkListeners();

  clearTimers();
  slowId = setTimeout(() => setState({ slow: true }), SLOW_AFTER_MS);
  timeoutId = setTimeout(() => {
    if (state.status !== "starting") return;
    setPhase("STARTUP_TIMEOUT", { lastPhase: state.phase });
    // Fail-safe: never leave the user on an empty screen. Offline gets the
    // offline screen, otherwise we render the app shell anyway so cached /
    // partial data can display and every section shows its own retry UI.
    if (isOffline()) {
      setState({ status: "offline", error: `Startup timed out during ${state.phase}` });
    } else {
      setState({ status: "ready", error: `Startup timed out during ${state.phase}` });
      setPhase("APP_READY");
    }
  }, BOOT_TIMEOUT_MS);

  try {
    // Native init runs in parallel — it must never gate auth restoration.
    void initNativeSafely();
    await restoreAuthSafely();
    setPhase("MINIMUM_DATA_LOAD_STARTED");
    // Nothing else is essential: banners, notices, settings, games, wallet,
    // FCM token, update config and results all load lazily inside the pages.
    setPhase("MINIMUM_DATA_LOAD_COMPLETED");
  } catch (error) {
    setPhase("STARTUP_ERROR", error);
    setState({ error: error instanceof Error ? error.message : String(error) });
  } finally {
    clearTimers();
    if (isOffline() && state.error) {
      setState({ status: "offline" });
    } else {
      setState({ status: "ready" });
      setPhase("ROUTER_READY");
      setPhase("APP_READY");
    }
    if (typeof window !== "undefined") {
      (window as any).__BC_BOOTED = true;
    }
  }
}

/** Singleton: StrictMode double-effects / rerenders cannot boot twice. */
export function bootstrapOnce(): Promise<void> {
  if (!bootstrapPromise) bootstrapPromise = performBootstrap();
  return bootstrapPromise;
}

/** Manual retry from the startup / offline / error screen. */
export function retryBootstrap(): Promise<void> {
  bootstrapPromise = performBootstrap();
  return bootstrapPromise;
}

function installNetworkListeners() {
  if (netListenersInstalled || typeof window === "undefined") return;
  netListenersInstalled = true;
  window.addEventListener("online", () => {
    console.info("[boot] network online");
    if (state.status === "offline") void retryBootstrap();
  });
  window.addEventListener("offline", () => {
    console.info("[boot] network offline");
  });
}
